// @ts-nocheck
import { Radio } from "@shared/antd/radio";
import { Spin } from "@shared/antd/spin";
import { Table } from "@shared/antd/table";
import React from "react";
import { ColumnProps, TableProps } from "antd/lib/table";
import { Link, useLocation } from "react-router-dom";
import { usePath } from "../../../../path-context";
import { ROUTES } from "../../../mobile-routes";

export interface RecursiveRecord {
  id?: string;
  isOpened?: boolean;
  childs?: RecursiveRecord[];
  children?: RecursiveRecord[];
  key?: string;
}

export interface TableAdaptiveProps<T extends RecursiveRecord> extends TableProps<T> {
  isNeedMobile?: boolean;
  isNeedLink?: boolean;
  urlKey?: keyof typeof ROUTES;
  isAdaptive?: boolean;
  mobileSelectable?: boolean;
  onExpandChild?: (key: string) => void;
}

export const TableAdaptive = <T extends RecursiveRecord>(props: TableAdaptiveProps<T>) => {
  const { isNeedMobile = false, isAdaptive = false, ...tableProps } = props;
  const location = useLocation();
  const isWebview = location.pathname.includes("mobile");
  const isMobile = isNeedMobile && isWebview;

  const mobileColumn = tableProps.columns?.find(item => item.key === "mobile") as ColumnProps<T> | undefined;

  if (mobileColumn?.render && tableProps.dataSource && isAdaptive) {
    return (
      <div className="flex flex-col gap-4">
        {renderRecords(tableProps.dataSource, mobileColumn, 0, props.onExpandChild)}
      </div>
    );
  }

  const columns = tableProps.columns?.filter(column => column.key !== "mobile") as ColumnProps<T>[];

  return (
    <>
      {isMobile
        ? <ElementTable<T> {...props} columns={columns} />
        : <Table<T> {...tableProps} columns={columns} />}
    </>
  );
};

export const ElementTable = <T extends RecursiveRecord>(
  props: TableAdaptiveProps<T> & { mobileSelectable?: boolean },
) => {
  const {
    dataSource = [],
    loading,
    columns = [],
    isNeedLink = false,
    urlKey,
    rowSelection,
    rowKey,
    mobileSelectable = false,
  } = props;

  const isSpinning = typeof loading === "boolean" ? loading : !!loading?.spinning;
  const { buildPath, getPath } = usePath();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 my-2">
      {dataSource.map((item, index) => {
        const key = resolveRowKey(rowKey, item, index) as string;
        const selectedKeys = rowSelection?.selectedRowKeys as RowKey[] | undefined;
        const isChecked = selectedKeys?.includes(key);

        const handleSelect = () => {
          if (mobileSelectable && rowSelection?.onChange) {
            rowSelection.onChange([key], [item]);
          }
        };

        const CardContent = (
          <div
            className={`bg-white rounded-xl shadow-md p-4 flex flex-col gap-2 border border-gray-100 ${
              mobileSelectable ? "cursor-pointer" : ""
            }`}
            onClick={handleSelect}
          >
            {mobileSelectable && (
              <div className="mb-2">
                <Radio checked={isChecked} onChange={handleSelect} />
              </div>
            )}

            {columns.map((column, colIndex) => {
              const value = item[column.dataIndex as keyof T];
              const content = column.render
                ? column.render(value, item, index)
                : value;

              return (
                <div key={colIndex} className="text-sm">
                  <span className="font-bold text-gray-600">{column.title}:</span>
                  <div className="text-gray-800">{content}</div>
                </div>
              );
            })}
          </div>
        );

        return (
          <Spin key={String(key)} spinning={isSpinning}>
            {isNeedLink && urlKey ? (
              <Link to={`/${buildPath(getPath(urlKey), { entityId: item.id })}`}>
                {CardContent}
              </Link>
            ) : (
              CardContent
            )}
          </Spin>
        );
      })}
    </div>
  );
};

function renderRecords<T extends RecursiveRecord>(
  data: T[],
  mobileColumn: ColumnProps<T>,
  level = 0,
  onExpandChild: (key: string) => void,
): React.ReactNode {
  return data?.map((record, index) => {
    const value = record[mobileColumn.dataIndex as keyof T];
    const hasChildren = Array.isArray(record?.childs) && record?.childs?.length > 0 || Array.isArray(record?.children) && record?.children?.length > 0;
    const isExpanded = record.isOpened;

    return (
      <div key={record.key} className="ml-[3px] mt-2">
        <div className={"w-full flex gap-2"}>
          <div className="w-full flex items-center gap-2">
            {mobileColumn.render?.(value, record, index)}
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="pl-2 border-l border-gray-400">
            {renderRecords(record.childs as T[] || record.children as T[], mobileColumn, level + 1, onExpandChild)}
          </div>
        )}
      </div>
    );
  });
}


type RowKey = string | number;

function resolveRowKey<T>(
  rowKey: string | ((record: T, index: number) => string) | undefined,
  record: T,
  index: number,
): RowKey {
  if (typeof rowKey === "function") return rowKey(record, index);
  if (typeof rowKey === "string") return (record as any)[rowKey];
  return (record as any).id ?? index;
}
